import { FunctionComponent, ReactNode, SVGProps } from "react";
import {
  Select as AriaSelect,
  SelectProps as AriaSelectProps,
  Button,
  ListBox,
  ListBoxItemProps,
  ListLayout,
  SelectValue,
  ValidationResult,
  Virtualizer,
  composeRenderProps,
} from "react-aria-components";
import { ChevronDown } from "lucide-react";
import { cn, composeTailwindRenderProps } from "@/utils/helpers";
import { Description, FieldError, Label } from "./Field";
import { KitDropdownItem, KitDropdownSection, KitDropdownSectionProps } from "./ListBox";
import { kitDropdownTriggerClass } from "./dropdownStyles";
import { KitPopover } from "./Popover";

export interface KitSelectProps<T extends object> extends Omit<AriaSelectProps<T>, "children"> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  children: ReactNode | ((item: T) => ReactNode);
  layoutOptions?: { rowHeight: number; gap?: number; padding?: number };
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  buttonClassName?: string;
  popoverClassName?: string;
}

export function KitSelect<T extends object>({
  label,
  description,
  errorMessage,
  children,
  items,
  layoutOptions,
  icon: Icon,
  buttonClassName,
  popoverClassName,
  ...props
}: KitSelectProps<T>) {
  const ariaLabel = props["aria-label"] || label || props.placeholder || "Select";

  return (
    <AriaSelect
      {...props}
      className={composeTailwindRenderProps(props.className, "group flex flex-col gap-2")}
      aria-label={props["aria-label"] ?? (label ? undefined : ariaLabel)}
    >
      {label && <Label>{label}</Label>}
      <Button
        className={composeRenderProps(buttonClassName, (cls, { isDisabled }) =>
          cn(
            kitDropdownTriggerClass,
            // Select (library, style B): a 44px capsule in control glass.
            "co-glass-control flex h-11 w-full min-w-[160px] items-center gap-2.5 rounded-pill px-3.5 text-start text-[14px] font-medium text-co-text",
            isDisabled && "cursor-not-allowed opacity-50",
            cls,
          ),
        )}
      >
        {Icon && <Icon aria-hidden className="size-3.5 shrink-0 text-co-text" />}
        <SelectValue className="flex-1 truncate data-[placeholder]:text-co-faint" />
        <ChevronDown aria-hidden className="size-4 shrink-0 text-co-muted" />
      </Button>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <KitPopover className={cn("min-w-(--trigger-width)", popoverClassName)}>
        {layoutOptions ? (
          <Virtualizer layout={ListLayout} layoutOptions={layoutOptions}>
            <ListBox
              items={items}
              className="max-h-[inherit] overflow-auto p-1.5 outline-none"
            >
              {children}
            </ListBox>
          </Virtualizer>
        ) : (
          <ListBox
            items={items}
            className="max-h-[inherit] overflow-auto p-1.5 outline-none"
          >
            {children}
          </ListBox>
        )}
      </KitPopover>
    </AriaSelect>
  );
}

export function KitSelectItem(props: ListBoxItemProps) {
  return <KitDropdownItem {...props} />;
}

export function KitSelectSection<T extends object>(props: KitDropdownSectionProps<T>) {
  return <KitDropdownSection {...props} />;
}
